import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { contactInfoApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminContactInfo() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'address',
    label: '',
    value: '',
    icon: '',
    url: '',
    order_position: 0,
    is_active: true
  });

  // Fetch contacts
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await contactInfoApi.getAll();
      setContacts(data || []);
    } catch (err) {
      setError('Failed to load contacts: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await contactInfoApi.update(editingId, formData);
        setError('');
      } else {
        await contactInfoApi.create(formData);
        setError('');
      }
      resetForm();
      loadContacts();
    } catch (err) {
      setError('Failed to save contact: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (contact) => {
    setFormData(contact);
    setEditingId(contact.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact info?')) {
      try {
        await contactInfoApi.delete(id);
        loadContacts();
      } catch (err) {
        setError('Failed to delete contact: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      type: 'address',
      label: '',
      value: '',
      icon: '',
      url: '',
      order_position: 0,
      is_active: true
    });
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Chargement des informations de contact...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gérer les Informations de Contact</h2>
        <button
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Ajouter une Info Contact
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Modifier l\'Info Contact' : 'Ajouter une Nouvelle Info Contact'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="address">Adresse</option>
                    <option value="phone">Téléphone</option>
                    <option value="email">Email</option>
                    <option value="social">Réseau Social</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Position d'Ordre</label>
                  <input
                    type="number"
                    name="order_position"
                    value={formData.order_position}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Étiquette</label>
                  <input
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleInputChange}
                    placeholder="ex: Siège Social, Téléphone Principal"
                  />
                </div>
                <div className="form-group">
                  <label>Icône</label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    placeholder="ex: fi-map-pin, fi-phone"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Valeur *</label>
                <input
                  type="text"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  required
                  placeholder="ex: contact@iamar.ma, +212 6XX XXX XXX"
                />
              </div>

              <div className="form-group">
                <label>URL (optionnel)</label>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Actif
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Sauvegarder l'Info Contact
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contacts Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Étiquette</th>
              <th>Valeur</th>
              <th>Position</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr><td colSpan="6" className="text-center">Aucune information de contact trouvée</td></tr>
            ) : (
              contacts.map(contact => (
                <tr key={contact.id}>
                  <td>{contact.type}</td>
                  <td>{contact.label}</td>
                  <td>{contact.value}</td>
                  <td>{contact.order_position}</td>
                  <td>{contact.is_active ? 'Actif' : 'Inactif'}</td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(contact)}
                      title="Modifier"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(contact.id)}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}